import { z } from "zod"
import { ProductSchema } from "../pos"
import { LegacyCompatibleDiscountSchema } from "./discount"
import { ProductChoiceSchema } from "./evaluated-service-bike-part"
import { PartActionUrgency } from "./part-action-urgency"
import { PartFunction } from "./part-function"
import { PartGrade } from "./part-grade"
import { ProposalLevel } from "./proposal-level-kind"
import { ServiceAction } from "./service-part-action"
import { ServiceProposalUpsellSchema } from "./upsell"

export const RequiredWashSchema = z.object({
  originalWashId: z.string(),
  name: z.string(),
})
export type RequiredWash = z.infer<typeof RequiredWashSchema>

export const RequiredPartActionSchema = z.object({
  partId: z.string(),
  partName: z.string(),
  action: z.nativeEnum(ServiceAction),
})
export type RequiredPartAction = z.infer<typeof RequiredPartActionSchema>

export const RequiredManualServiceActionSchema = z.object({
  id: z.string(),
  title: z.string(),
})
export type RequiredManualServiceAction = z.infer<
  typeof RequiredManualServiceActionSchema
>

export const gradeToWearLevel = (grade: PartGrade): number => {
  switch (grade) {
    case PartGrade.ZERO:
      return 0
    case PartGrade.ONE:
      return 10
    case PartGrade.TWO:
      return 20
    case PartGrade.THREE:
      return 30
    case PartGrade.FOUR:
      return 40
    case PartGrade.FIVE:
      return 50
    case PartGrade.SIX:
      return 60
    case PartGrade.SEVEN:
      return 70
    case PartGrade.EIGHT:
      return 80
    case PartGrade.NINE:
      return 90
    case PartGrade.TEN:
      return 100
  }
}

/**
 * The info box the customer sees on a component the mechanic marked as not
 * working (optimally). Built in the API, in Swedish, like the rest of the
 * proposal's server-side texts.
 */
export const ProposalFunctionNoticeSchema = z.object({
  text: z.string(),
  /** Shown once the customer deselects the work. Null when there is none. */
  deselectedText: z.string().nullable(),
})
export type ProposalFunctionNotice = z.infer<
  typeof ProposalFunctionNoticeSchema
>

export const ServiceProposalPartActionSchema = z.object({
  action: z.nativeEnum(ServiceAction),
  actionLabel: z.string().optional(),
  laborPrice: z.number(),
  laborPriceDiscount: LegacyCompatibleDiscountSchema.nullable(),
  partId: z.string(),
  partName: z.string(),
  partImage: z.string(),
  // Null when the action needs no grade, so there's no wear to show.
  partGrade: z.nativeEnum(PartGrade).nullable(),
  comment: z.string().nullable(),
  requiredWash: RequiredWashSchema.nullable(),
  requiredManualServiceActions: z.array(RequiredManualServiceActionSchema),
  requiredPartActions: z.array(RequiredPartActionSchema),
  products: z.array(ProductSchema),
  productChoices: z.array(ProductChoiceSchema),
  isApprovedViaReception: z.boolean(),
  // Defaulted so proposals sent before this field existed (stored as JSON in
  // SentServiceProposal.payload) still parse on read.
  isMandatory: z.boolean().default(false),
  /**
   * The component is legally required equipment (lights, bell, reflectors), so
   * the customer sees a "Lagkrav" badge. Absent on older payloads.
   */
  isLegalRequirement: z.boolean().optional(),
  /**
   * CAN_WAIT work is left out of the "Trafiksäker" preset. Defaulted so
   * proposals sent before the presets existed parse as all-urgent.
   */
  urgency: z.nativeEnum(PartActionUrgency).default(PartActionUrgency.NOW),
  /**
   * Both defaulted so proposals sent before the mechanic could mark a
   * component's function still parse. The notice is null when it works.
   */
  partFunction: z.nativeEnum(PartFunction).default(PartFunction.WORKS),
  functionNotice: ProposalFunctionNoticeSchema.nullable().default(null),
})
export type ServiceProposalPartAction = z.infer<
  typeof ServiceProposalPartActionSchema
>

export const ServiceProposalManualActionSchema = z.object({
  id: z.string(),
  title: z.string(),
  laborPrice: z.number(),
  laborPriceDiscount: LegacyCompatibleDiscountSchema.nullable(),
  comment: z.string().nullable(),
  requiredWash: RequiredWashSchema.nullable(),
  requiredManualServiceActions: z.array(RequiredManualServiceActionSchema),
  requiredPartActions: z.array(RequiredPartActionSchema),
  products: z.array(ProductSchema),
  productChoices: z.array(ProductChoiceSchema),
  // Defaulted so proposals sent before this field existed (stored as JSON in
  // SentServiceProposal.payload) still parse on read.
  isMandatory: z.boolean().default(false),
})
export type ServiceProposalManualAction = z.infer<
  typeof ServiceProposalManualActionSchema
>

export const FreshServiceProposalWashSchema = z.object({
  originalWashId: z.string(),
  snapshotWashId: z.string(),
  name: z.string(),
  price: z.number(),
  // Defaulted so proposals sent before wash discounts existed (stored as JSON
  // in SentServiceProposal.payload) still parse on read.
  laborPriceDiscount: LegacyCompatibleDiscountSchema.nullable().default(null),
  description: z.string().nullable(),
  requiredManualServiceActions: z.array(RequiredManualServiceActionSchema),
  requiredPartActions: z.array(RequiredPartActionSchema),
})
export type FreshServiceProposalWash = z.infer<
  typeof FreshServiceProposalWashSchema
>

export const FreshServiceProposalWashAlternativeSchema =
  FreshServiceProposalWashSchema.extend({
    isRecommended: z.boolean(),
  })
export type FreshServiceProposalWashAlternative = z.infer<
  typeof FreshServiceProposalWashAlternativeSchema
>

export const FreshServiceProposalWashAlternativesSchema = z.object({
  id: z.string(),
  isMandatory: z.boolean(),
  comment: z.string().nullable(),
  alternatives: z.array(FreshServiceProposalWashAlternativeSchema),
})
export type FreshServiceProposalWashAlternatives = z.infer<
  typeof FreshServiceProposalWashAlternativesSchema
>

export const ServiceProposalFeeSchema = z.object({
  snapshotId: z.string(),
  originalFeeId: z.string(),
  title: z.string(),
  cost: z.number(),
  description: z.string().nullable(),
})
export type ServiceProposalFee = z.infer<typeof ServiceProposalFeeSchema>

export const ServiceProposalPackageSchema = z.object({
  originalPackageId: z.string(),
  snapshotPackageId: z.string(),
  name: z.string(),
  laborPrice: z.number(),
  laborPriceDiscount: LegacyCompatibleDiscountSchema.nullable(),
  includedMaterialCost: z.number().nullable(),
  wash: FreshServiceProposalWashSchema.nullable(),
  partActions: z.array(ServiceProposalPartActionSchema),
  isApprovedViaReception: z.boolean(),
  /**
   * The optimizer swapped this package out for a cheaper combination. It is
   * still on the proposal — the customer sees the card and can put it back —
   * but nothing selects it for them.
   *
   * Defaulted: proposals sent before this flag existed are re-parsed with this
   * same schema on read, and their stored packages have no such key.
   */
  isDroppedByOptimizer: z.boolean().default(false),
})
export type ServiceProposalPackage = z.infer<
  typeof ServiceProposalPackageSchema
>

export const ServiceProposalDependenciesSchema = z.object({
  partActionsWithDependencies: z.array(ServiceProposalPartActionSchema),
  manualActionsWithDependencies: z.array(ServiceProposalManualActionSchema),
  washWithDependencies: FreshServiceProposalWashSchema.nullable(),
})
export type ServiceProposalDependencies = z.infer<
  typeof ServiceProposalDependenciesSchema
>

export const FreshServiceProposalSchema = z.object({
  packages: z.array(ServiceProposalPackageSchema),
  wash: FreshServiceProposalWashSchema.nullable(),
  washAlternatives: FreshServiceProposalWashAlternativesSchema.nullable(),
  partActions: z.array(ServiceProposalPartActionSchema),
  manualActions: z.array(ServiceProposalManualActionSchema),
  fees: z.array(ServiceProposalFeeSchema),
  dependencies: ServiceProposalDependenciesSchema,
  physicalWorkshopActions: z.array(ServiceProposalPartActionSchema),
  /**
   * Equipment the mechanic found missing, offered in the customer's "Tillval"
   * step. Null when the proposal has no such step (older proposals, or one
   * accepted on the customer's behalf).
   */
  upsell: ServiceProposalUpsellSchema.nullish(),
  createdAt: z.coerce.date<Date>(),
})
export type FreshServiceProposal = z.infer<typeof FreshServiceProposalSchema>

export const ProductDecisionSchema = z.record(
  z.guid(), // key: choiceId
  z.guid(), // value: productId
)

export const SelectedPartActionSchema = z.object({
  partId: z.guid(),
  productDecisions: ProductDecisionSchema,
})

export const SelectedManualActionSchema = z.object({
  id: z.guid(),
  productDecisions: ProductDecisionSchema,
})

export const SelectedProposalPackageSchema = z.object({
  originalPackageId: z.guid(),
  selectedPartActions: z.array(SelectedPartActionSchema),
  selectedWashOriginalId: z.guid().optional(),
})

export const SubmitFreshServiceProposalSchema = z.object({
  selectedPackages: z.array(SelectedProposalPackageSchema).default([]),
  selectedPartActions: z.array(SelectedPartActionSchema).default([]),
  selectedManualActions: z.array(SelectedManualActionSchema).default([]),
  selectedWashOriginalId: z.guid().optional(),
  /**
   * The customer answered an optional wash choice with "Ingen tvätt". Distinct
   * from leaving `selectedWashOriginalId` unset, which means they have not
   * answered yet — and which still previews the mechanic's recommendation so
   * the total reads as a "från"-price rather than a promise.
   */
  isWashDeselected: z.boolean().optional(),
  /** Offered upsell products the customer added, at most one per offer. */
  selectedUpsellProductIds: z.array(z.guid()).optional(),
  /**
   * The customer approved the "Serviceförslag" step and moved on to
   * "Tillval". Only meaningful for the saved in-progress selection.
   */
  hasCompletedServiceStep: z.boolean().optional(),
  /**
   * "Trafiksäker" or "Toppskick": which of the two proposals the customer is
   * answering. Absent on proposals without levels and on selections saved
   * before them, which read as Toppskick.
   */
  level: z.nativeEnum(ProposalLevel).optional(),
  /**
   * The customer is answering "Anpassad service": Toppskick with what they
   * took out in "Anpassa service" (`removedPartIds`,
   * `removedManualActionIds`). `level` is then Toppskick.
   */
  isCustomService: z.boolean().optional(),
  /**
   * Components the customer took out of "Anpassad service". Remembered while
   * they answer one of the presets, which stay as sent, so the "Anpassad
   * service" card is still there to go back to.
   */
  removedPartIds: z.array(z.guid()).optional(),
  /** Manual actions taken out of "Anpassad service", remembered the same way. */
  removedManualActionIds: z.array(z.guid()).optional(),
})

export type SubmitFreshServiceProposal = z.infer<
  typeof SubmitFreshServiceProposalSchema
>
export type SelectedProposalPackage = z.infer<
  typeof SelectedProposalPackageSchema
>
export type SelectedPartAction = z.infer<typeof SelectedPartActionSchema>
export type SelectedManualAction = z.infer<typeof SelectedManualActionSchema>
